/**
 * Purchase Configurator
 * ----------------------------------------------------------------------------
 * Multi-stage bundle configurator overlay/drawer.
 *
 * The flow is driven by a stage registry injected from Liquid as
 * `window.configuratorData.stages`. Each stage is
 * `{ id, title, heading, kind, source, required, price, options }`.
 * Navigation and validation are stage-driven — there are no hardcoded step
 * numbers in this file.
 *
 * Stage IDs are numeric strings ("1", "2", "3", …) and identify a stage's
 * position in the flow. Selection state is stored under semantic keys
 * (frame / upholstery / bp_<productId>_<position> / accessories) so
 * re-ordering stages doesn't move the meaning of any selection.
 *
 * Recognised `kind` values:
 *   'variant-options' → one or more swatch pickers backed by a product's
 *                       variant options. `stage.source` selects which
 *                       product(s) they come from:
 *                         'product'         → the page's main product
 *                         'bundle_products' → every product in the
 *                                             `custom.bundle_products`
 *                                             list metafield, one set of
 *                                             swatch groups per product
 *                       Each entry of `stage.options` is `{ key, label,
 *                       position?, productId? }` and writes to
 *                       `state.selections[key]`. For
 *                       source='bundle_products', `productId` scopes the
 *                       option to a specific partner and `position`
 *                       lets us look up `option<position>` on that
 *                       partner's variant rows.
 *   'optional-items'  → metafield-driven list (`bundle_optional_items`)
 *                       of optional products. Each card has a toggle and a
 *                       single combined <select> whose options map 1:1 to
 *                       the product's variants. State lives at
 *                       `state.selections.accessories[productId]`. Always
 *                       non-required — the customer can skip the stage.
 *   'review'          → final summary + checkout (no own state)
 *
 * Adding a new stage of an existing kind = no JS change required.
 * Adding a new kind = handle it in `initSelections`, `isStageValid`,
 *   `buildSelectionsSummary` and (where relevant) `calculateTotal`.
 *
 * Pricing
 * -----------------------------------------------------------------------------
 * No variant is auto-selected on load — the customer must explicitly pick
 * frame + upholstery (Stage 1) and every bundle_products option group
 * (Stage 2) before the corresponding price contributes to the running
 * total. The footer total displayed on every non-review stage is the
 * same `calculateTotal()` value, so navigating backwards never resets
 * the displayed price.
 *
 * Bundle saving is driven by two product metafields:
 *   custom.bundle_discount_value : numeric value (e.g. 10 or 20)
 *   custom.bundle_discount_type  : 'Percentage' or 'Fixed'
 * The saving is applied to the sum of the main variant + every resolved
 * bundle_products variant once Stage 1 and every Stage 2 group are fully
 * chosen, and clamped to that eligible amount.
 */

(function () {
  'use strict';

  // =========================================================================
  // State
  // =========================================================================

  const state = {
    stages: [],            // Stage registry (from window.configuratorData.stages)
    currentStageIndex: 0,
    selections: {},        // Keyed by semantic name (frame / upholstery /
                           // bp_<productId>_<position> / accessories), NOT
                           // by stage.id.
  };

  // Resolved from window.configuratorData (injected via Liquid)
  let productData = null;
  let variants = [];
  let basePrice = 0;

  // Bundle products (Stage 2) — every product in `custom.bundle_products`.
  // Each entry is { product, variants } so we can resolve a variant for a
  // given product without re-querying. `bundleProductsById` is a flat index
  // keyed by product id for cheap lookups in hot paths (validation, total).
  let bundleProducts = []; // [{ product, variants }]
  let bundleProductsById = {}; // productId → { product, variants }

  // The bundle product the customer has most recently interacted with.
  // Used purely to pick which partner's hero image surfaces in the image
  // panel while the customer is configuring Stage 2 — falls back to the
  // first bundle product when nothing has been touched yet.
  let activeBundleProductId = null;

  let optionalItems = []; // [{ product, variants }]
  let optionalItemsById = {}; // productId → { product, variants }

  // Variant-mapping table: mainVariantId → array of bundle-product variant
  // ids. Populated from the `custom.bundle_product_variants` variant
  // metafield (a list of variant refs) on the main product.
  //
  // Matching is positional → identity:
  //   • Positional for the partner: `mappedList[i]` constrains
  //     `bundleProducts[i]`. The merchant is responsible for keeping the
  //     order of `custom.bundle_product_variants` in lockstep with
  //     `custom.bundle_products` — variant ref at index 0 must belong to
  //     bundle product 0, index 1 to bundle product 1, etc.
  //   • Identity for the variant inside that partner: JS finds the
  //     variant row in `bundleProducts[i].variants` whose id matches
  //     `mappedList[i]` so it can read that variant's option values and
  //     constrain the swatches accordingly.
  //
  // Edge cases:
  //   • `null` slot in the list           → bundle product at that index
  //                                         stays free-choice.
  //   • List shorter than bundleProducts  → tail bundle products are
  //                                         unconstrained.
  //   • Mapped id doesn't belong to the
  //     bundle product at that index      → ignored (silent failure);
  //                                         that partner stays
  //                                         free-choice rather than
  //                                         picking up the wrong
  //                                         constraint.
  //   • Main variant has no list at all   → every bundle product is
  //                                         free-choice.
  //
  // Shape: { [mainVariantId]: [bpVariantId|null, bpVariantId|null, ...] }
  let bundleProductVariantsMap = {};

  // "Included in your purchase" items, extracted from the PDP's
  // `.included-in-purchase` panel during `populateIncludedInPurchase()`.
  // Used by `buildSelectionsSummary()` to render text rows in the review
  // pane so the summary stays in sync with whatever the merchant has
  // configured on the `included_in_purchase` block — no hardcoded titles.
  // Shape: [{ title, isFree, priceText }].
  let includedInPurchaseItems = [];

  // Pricing config — sourced from window.configuratorData (injected by Liquid).
  // `bundleDiscount` mirrors the two `custom.bundle_discount_*` metafields:
  //   value : raw merchant-facing number (e.g. `10` or `20`)
  //   type  : 'Percentage' or 'Fixed' (case-insensitive)
  let currencyCode = 'GBP';
  let bundleDiscount = { value: 0, type: '' };
  let moneyFormatter = null;

  // Option positions (0-indexed) inside the main product's variant options.
  // Bundle product option positions travel on each stage option entry
  // instead — each partner can have any number of options.
  let frameOptionIndex = -1;
  let upholsteryOptionIndex = -1;

  // =========================================================================
  // Init
  // =========================================================================

  function init() {
    if (!window.configuratorData) return;

    productData = window.configuratorData.product;
    variants = window.configuratorData.variants || [];
    basePrice = window.configuratorData.basePrice || 0;

    currencyCode =
      window.configuratorData.currency ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'GBP';

    const rawDiscount = window.configuratorData.bundleDiscount || {};
    bundleDiscount = {
      value:
        rawDiscount.value == null || rawDiscount.value === ''
          ? 0
          : Number(rawDiscount.value) || 0,
      type: (rawDiscount.type || '').toString().trim().toLowerCase(),
    };

    bundleProducts = window.configuratorData.bundleProducts || [];
    bundleProductsById = {};
    bundleProducts.forEach(function (entry) {
      if (entry && entry.product && entry.product.id != null) {
        bundleProductsById[String(entry.product.id)] = entry;
      }
    });
    activeBundleProductId =
      bundleProducts.length > 0 && bundleProducts[0].product
        ? String(bundleProducts[0].product.id)
        : null;

    optionalItems = window.configuratorData.bundleOptionalItems || [];
    optionalItemsById = {};
    optionalItems.forEach(function (entry) {
      if (entry && entry.product && entry.product.id != null) {
        optionalItemsById[String(entry.product.id)] = entry;
      }
    });

    bundleProductVariantsMap = window.configuratorData.bundleProductVariantsMap || {};

    state.stages = window.configuratorData.stages || [];

    initSelections();
    resolveOptionIndices();
    bindEvents();
    updateStageIndicator();
    updateFooterPrices();
    updateStageLockState();
    populateIncludedInPurchase();
  }

  // Clone the PDP's "Included in your purchase" panel into the review pane.
  //
  // The source markup is rendered by the main-product section's
  // `included_in_purchase` block (see `snippets/included-in-purchase.liquid`),
  // so whatever the merchant has configured on the PDP is the single source
  // of truth — we never duplicate the data into the configurator's own
  // schema. Only the first matching `.included-in-purchase` outside the
  // configurator is used, so re-opening the configurator doesn't pick up
  // a previously-cloned copy.
  //
  // After cloning we:
  //   1. Wipe inline state (`max-height`, `aria-expanded`, `is-expanded`,
  //      toggle text) so the clone starts in the collapsed default rather
  //      than mirroring whatever interaction state the PDP copy is in.
  //   2. Re-instantiate `IncludedInPurchase` (exposed on `window` by
  //      `assets/included-in-purchase.js`) so the See More / See Less
  //      toggle works on the clone.
  //   3. Unhide the placeholder. If no source element exists (block not
  //      configured on the product), the placeholder stays `hidden`.
  function populateIncludedInPurchase() {
    const placeholder = document.getElementById('configurator-included-in-purchase');
    if (!placeholder) return;

    // Already populated on a previous init pass — leave it alone.
    if (placeholder.firstElementChild) return;

    const source = document.querySelector(
      '.included-in-purchase:not(#configurator-included-in-purchase .included-in-purchase)'
    );
    if (!source) return;

    // Extract a normalised list of items while we have the source in hand,
    // so `buildSelectionsSummary` can render text rows without re-querying
    // (or coupling itself to) the DOM. Items with the merchant's "Free"
    // checkbox ticked emit a `<span class="...--free">Free</span>` value;
    // others fall back to the merchant-entered price string.
    includedInPurchaseItems = Array.from(
      source.querySelectorAll('.included-in-purchase__item')
    ).map(function (item) {
      const titleEl = item.querySelector('.included-in-purchase__item-title');
      const priceEl = item.querySelector('.included-in-purchase__item-price-value');
      const freeEl = item.querySelector('.included-in-purchase__item-price-free');
      return {
        title: titleEl ? titleEl.textContent.trim() : '',
        isFree: !!freeEl,
        priceText: priceEl ? priceEl.textContent.trim() : '',
      };
    });

    if (typeof window.IncludedInPurchase === 'function') {
      new window.IncludedInPurchase(clone);
    }
  }

  function initSelections() {
    // Initialise the selection shape using semantic keys derived from each
    // stage's options, so downstream code can read state.selections by
    // meaning (e.g. state.selections.frame,
    // state.selections.bp_<productId>_<position>) without depending on
    // where the stage sits in the flow.
    state.stages.forEach(function (stage) {
      if (stage.kind !== 'variant-options') return;
      (stage.options || []).forEach(function (opt) {
        state.selections[opt.key] = null;
      });
    });

    // Optional items (Stage 3) — keyed by product id. Each entry tracks
    // whether the customer has toggled the card on and which variant they
    // picked from the single combined dropdown. Products with only a
    // default variant get their lone variant id pre-resolved here so a
    // toggle is enough to include them.
    state.selections.accessories = {};
    optionalItems.forEach(function (entry) {
      if (!entry || !entry.product) return;
      const id = String(entry.product.id);
      const product = entry.product;
      const variants = entry.variants || [];
      const initialVariantId =
        product.has_only_default_variant && variants.length ? variants[0].id : null;
      const initialPrice =
        product.has_only_default_variant && variants.length
          ? variants[0].price
          : product.price;
      state.selections.accessories[id] = {
        included: false,
        variantId: initialVariantId,
        title: product.title,
        price: initialPrice,
      };
    });
  }

  // =========================================================================
  // Stage Registry Helpers
  // =========================================================================

  function getStageByIndex(idx) {
    return state.stages[idx] || null;
  }

  function getStageById(id) {
    for (let i = 0; i < state.stages.length; i++) {
      if (state.stages[i].id === id) return state.stages[i];
    }
    return null;
  }

  function getStageIndex(id) {
    for (let i = 0; i < state.stages.length; i++) {
      if (state.stages[i].id === id) return i;
    }
    return -1;
  }

  // =========================================================================
  // Validation
  //   - A stage is "valid" when its required selection(s) are made.
  //   - canAdvanceTo(targetIndex) ensures every preceding stage is also valid,
  //     so the user can never reach stage N without satisfying 0..N-1.
  // =========================================================================

  function isStageValid(stage) {
    if (!stage) return true;

    // Optional-items stages aren't strictly "required" — the customer can
    // leave every card untoggled and still proceed. But once a card has
    // been included, the dropdown variant MUST be chosen; otherwise we'd
    // forward an unresolved entry to the summary and a `null` variant id
    // into the cart permalink. Toggling the card back off clears the
    // requirement, so removal is always allowed.
    if (stage.kind === 'optional-items') {
      return Object.values(state.selections.accessories || {}).every(function (acc) {
        if (!acc || !acc.included) return true;
        return acc.variantId != null;
      });
    }

    if (!stage.required) return true;

    if (stage.kind === 'variant-options') {
      // Every option group in the stage must have a selection.
      return (stage.options || []).every(function (opt) {
        return state.selections[opt.key] != null;
      });
    }
    return true;
  }

  function canAdvanceTo(targetIndex) {
    if (targetIndex < 0 || targetIndex >= state.stages.length) return false;
    for (let i = 0; i < targetIndex; i++) {
      if (!isStageValid(state.stages[i])) return false;
    }
    return true;
  }

  function updateStageLockState() {
    document.querySelectorAll('.configurator-step-tab').forEach(function (tab) {
      const idx = getStageIndex(tab.dataset.tabStage);
      const reachable = canAdvanceTo(idx);
      tab.classList.toggle('is-disabled', !reachable);
      tab.setAttribute('aria-disabled', reachable ? 'false' : 'true');
    });

    document.querySelectorAll('.configurator-step__next').forEach(function (btn) {
      const idx = getStageIndex(btn.dataset.nextStage);
      btn.disabled = !canAdvanceTo(idx);
    });
  }

  // =========================================================================
  // Option Index Resolution
  //   Map the product's option positions (option1/option2/option3) to the
  //   logical "frame" and "upholstery" axes, so we can find the right variant
  //   when both selections are made.
  // =========================================================================

  function resolveOptionIndices() {
    if (!productData || !productData.options) return;

    productData.options.forEach(function (optionName, index) {
      const lower = optionName.toLowerCase();
      if (lower.includes('frame') || lower.includes('wood')) {
        frameOptionIndex = index;
      } else if (
        lower.includes('upholstery') ||
        lower.includes('colour') ||
        lower.includes('color')
      ) {
        upholsteryOptionIndex = index;
      }
    });
  }

  // =========================================================================
  // Event Binding
  // =========================================================================

  function bindEvents() {
    // Open / close
    const openBtn = document.getElementById('open-configurator');
    if (openBtn) openBtn.addEventListener('click', openConfigurator);

    const closeBtn = document.getElementById('close-configurator');
    if (closeBtn) closeBtn.addEventListener('click', closeConfigurator);

    const backdrop = document.getElementById('configurator-backdrop');
    if (backdrop) backdrop.addEventListener('click', closeConfigurator);

    const grabHandle = document.getElementById('configurator-grab-handle');
    if (grabHandle) grabHandle.addEventListener('click', closeConfigurator);

    const imagePanel = document.querySelector('.purchase-configurator__image-panel');
    if (imagePanel) imagePanel.addEventListener('click', closeConfigurator);

    // Tabs — click to navigate (only if reachable)
    document.querySelectorAll('.configurator-step-tab').forEach(function (tab) {
      tab.addEventListener('click', function () {
        const idx = getStageIndex(tab.dataset.tabStage);
        if (idx < 0 || !canAdvanceTo(idx)) return;
        goToStage(idx);
      });
    });

    // Next stage buttons (only if current + preceding stages are valid)
    document.querySelectorAll('.configurator-step__next').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        const idx = getStageIndex(btn.dataset.nextStage);
        if (idx < 0 || !canAdvanceTo(idx)) return;
        goToStage(idx);
      });
    });

    // Back stage buttons (always allowed)
    document.querySelectorAll('.configurator-step__back').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const idx = getStageIndex(btn.dataset.prevStage);
        if (idx >= 0) goToStage(idx);
      });
    });

    // Add-to-cart button (kept as `#configurator-checkout` for CSS continuity).
    const checkoutBtn = document.getElementById('configurator-checkout');
    if (checkoutBtn) checkoutBtn.addEventListener('click', handleAddToCart);

    // Variant-option swatches — `data-option` carries the option key
    // (the state key); the stage is implicit from the swatch's pane.
    document.querySelectorAll('.configurator-swatch[data-option]').forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        if (swatch.disabled) return;
        selectSwatch(swatch.dataset.option, swatch);
      });
    });

    // Optional-item cards (Stage 3) — click anywhere on the card (except
    // the dropdown itself) to toggle inclusion. Keyboard: Space / Enter.
    document.querySelectorAll('.configurator-card--optional').forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (e.target.closest('select, label, .select__select, .configurator-card__select-group')) {
          return;
        }
        toggleOptionalItem(card);
      });
      card.addEventListener('keydown', function (e) {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        if (e.target.closest('select, label, .select__select, .configurator-card__select-group')) return;
        e.preventDefault();
        toggleOptionalItem(card);
      });
    });

    // Optional-item dropdowns — one combined select per product; the
    // <option value> is the chosen variant id, so we just read it directly.
    document.querySelectorAll('select[data-optional-variant]').forEach(function (sel) {
      sel.addEventListener('change', function () {
        selectOptionalVariant(sel);
      });
      // Don't let card click bubble through the select wrapper.
      sel.addEventListener('click', function (e) { e.stopPropagation(); });
    });

    // Keyboard: close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeConfigurator();
    });
  }

  // =========================================================================
  // Open / Close
  // =========================================================================

  function openConfigurator() {
    const overlay = document.getElementById('purchase-configurator');
    if (!overlay) return;

    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('configurator-is-open');

    goToStage(0, true);
  }

  function closeConfigurator() {
    const overlay = document.getElementById('purchase-configurator');
    if (!overlay) return;

    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('configurator-is-open');
  }

  // =========================================================================
  // Stage Navigation
  // =========================================================================

  function goToStage(stageIndex, instant) {
    const stage = getStageByIndex(stageIndex);
    if (!stage) return;

    state.currentStageIndex = stageIndex;

    // Hide all stage panes
    document.querySelectorAll('.configurator-step').forEach(function (el) {
      el.style.display = 'none';
    });

    // Show target stage pane
    const target = document.querySelector(
      '.configurator-step[data-stage-id="' + stage.id + '"]'
    );
    if (target) {
      target.style.display = 'flex';
      if (!instant) {
        target.style.animation = 'none';
        target.offsetHeight; // reflow
        target.style.animation = '';
      }
    }

    updateStageIndicator();
    updateFooterPrices();
    updateStageLockState();
    updateProductImage();

    if (stage.kind === 'review') {
      buildReviewPage();
    }

    // Toggle review-mode class so CSS can hide the tab strip etc.
    const panel = document.querySelector('.purchase-configurator__panel');
    if (panel) {
      panel.classList.toggle('is-review', stage.kind === 'review');
    }

    // Swap the panel title between the generic copy and the stage's heading
    const titleEl = document.querySelector('.purchase-configurator__title');
    if (titleEl) {
      titleEl.textContent = stage.kind === 'review' ? stage.heading : 'Design Your Bed';
    }
  }

  // =========================================================================
  // Stage Indicator (tab strip)
  // =========================================================================

  function updateStageIndicator() {
    const currentStage = getStageByIndex(state.currentStageIndex);
    if (!currentStage) return;

    document.querySelectorAll('.configurator-step-tab').forEach(function (tab) {
      const idx = getStageIndex(tab.dataset.tabStage);
      tab.classList.remove('is-active', 'is-complete');
      tab.setAttribute('aria-selected', 'false');

      if (currentStage.kind === 'review') {
        tab.classList.add('is-complete');
      } else if (idx === state.currentStageIndex) {
        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');
      } else if (idx >= 0 && idx < state.currentStageIndex) {
        tab.classList.add('is-complete');
      }
    });
  }

  // =========================================================================
  // Variant-Option Stage (swatches)
  // =========================================================================

  function selectSwatch(optionKey, swatch) {
    const value = swatch.dataset.value;
    state.selections[optionKey] = value;

    // Only deselect siblings inside the same option group, not across
    // groups (so frame and upholstery don't deselect each other when they
    // share a stage).
    const container = swatch.closest('.configurator-options--swatches');
    if (container) {
      container.querySelectorAll('.configurator-swatch').forEach(function (s) {
        s.classList.remove('is-selected');
      });
    }
    swatch.classList.add('is-selected');

    // If this swatch belongs to a bundle product, mark that product as
    // the one driving the Stage 2 image panel. Liquid renders
    // `data-bundle-product-id` on every bundle swatch so this is a
    // cheap lookup with no string parsing.
    const bundleProductId = swatch.dataset.bundleProductId;
    if (bundleProductId) {
      activeBundleProductId = String(bundleProductId);
    }

    // Re-evaluate which Stage 2 swatches should be enabled — this is cheap
    // and idempotent, so it's simpler to run on every swatch change than to
    // try and detect "did the main variant identity actually move?".
    applyBundleProductVariantsMapping();

    updateProductImage();
    updateFooterPrices();
    updateStageLockState();
  }

  // Gate Stage 2's swatches based on the `custom.bundle_product_variants`
  // mapping (a list of variant refs) on the currently-resolved main
  // variant. Matching is positional → identity:
  //
  //   • Positional for the partner: `mappedList[i]` constrains
  //     `bundleProducts[i]`. The merchant maintains this correspondence
  //     by ordering `custom.bundle_product_variants` in lockstep with
  //     `custom.bundle_products` — variant ref at index 0 is the
  //     constraint for bundle product 0, index 1 for bundle product 1,
  //     and so on.
  //   • Identity for the variant inside the partner: we look up
  //     `mappedList[i]` inside `bundleProducts[i].variants` so we can
  //     read that specific variant's option values and disable swatches
  //     that don't match. (The metafield stores ids; the option values
  //     live on the variant rows we've already injected into JS.)
  //
  // Behaviour:
  //   • Resolves the main variant from `state.selections.frame` / `upholstery`.
  //   • Pulls the list of mapped variant ids out of
  //     `bundleProductVariantsMap[mainVariantId]`.
  //   • For each `(idx, mappedVariantId)` pair: locates the variant row in
  //     `bundleProducts[idx].variants` whose id matches, then disables
  //     every swatch in that product's option groups that doesn't match
  //     the resolved variant's `option<position>` (via
  //     `is-mapping-disabled` so we never trample Liquid-side
  //     "unavailable" disables).
  //   • `null` slots, slots past the end of the list, or slots whose
  //     mapped id doesn't belong to that bundle product → that partner
  //     stays free-choice (silent fallback rather than picking up the
  //     wrong constraint).
  //   • When the main variant has no list at all, re-enables anything we
  //     previously mapping-disabled — every bundle product is free-choice.
  //   • Any bp selection that is no longer enabled after constraint is
  //     cleared from state so validation / pricing reflect reality.
  function applyBundleProductVariantsMapping() {
    const bpStage = getBundleProductsStage();
    if (!bpStage) return;

    // Reset — undo previous mapping-driven disables so we start from a clean
    // slate. We deliberately don't touch swatches disabled by Liquid (those
    // lack the `is-mapping-disabled` marker class).
    document.querySelectorAll('.configurator-swatch.is-mapping-disabled').forEach(function (sw) {
      sw.classList.remove('is-mapping-disabled');
      sw.removeAttribute('disabled');
    });

    // Need both Stage 1 selections to resolve a main variant. Without them,
    // Stage 2 is unreachable anyway (Next is locked), so just bail.
    if (state.selections.frame == null || state.selections.upholstery == null) return;

    const mainVariant = findVariant(state.selections.frame, state.selections.upholstery);
    if (!mainVariant) return;

    const mappedList = bundleProductVariantsMap[String(mainVariant.id)];
    if (!mappedList || !mappedList.length) return; // no mapping → free choice everywhere

    // Walk every bundle product by position. We iterate `bundleProducts`
    // (not `mappedList`) so a list that's shorter than bundle_products
    // gracefully leaves the tail unconstrained.
    bundleProducts.forEach(function (entry, idx) {
      const mappedVariantId = mappedList[idx];
      if (mappedVariantId == null) return; // null / undefined slot → free choice
      if (!entry || !entry.product) return;

      // Resolve the mapped id to the actual variant row inside this
      // bundle product (the metafield stores ids; option values live on
      // the variant rows we already injected into JS). Loose equality
      // (`==`) because the JSON map can emit either strings or numbers
      // depending on Liquid — variant ids are plain integers so this is
      // always safe.
      const mappedVariant = (entry.variants || []).find(function (v) {
        return v.id == mappedVariantId;
      });
      if (!mappedVariant) return; // mapped id doesn't belong to this product → skip

      // Constrain only this bundle product's options — every option entry
      // on the stage carries a `productId` for exactly this reason.
      const productIdStr = String(entry.product.id);
      const ownedOptions = (bpStage.options || []).filter(function (opt) {
        return opt.productId != null && String(opt.productId) === productIdStr;
      });

      ownedOptions.forEach(function (opt) {
        const requiredValue = mappedVariant['option' + (opt.position || 1)];
        const swatches = document.querySelectorAll(
          '.configurator-swatch[data-option="' + opt.key + '"]'
        );
        swatches.forEach(function (sw) {
          if (sw.dataset.value === requiredValue) return;
          if (sw.hasAttribute('disabled')) return;
          sw.classList.add('is-mapping-disabled');
          sw.setAttribute('disabled', 'disabled');
        });
      });

      // Clear any bp selection (within the constrained product only) that's
      // now disabled — keeps `state.selections` honest so validation /
      // pricing react. Lookup is done in JS so we don't have to worry about
      // escaping option values that may contain CSS-special characters
      // (spaces, quotes, etc.).
      ownedOptions.forEach(function (opt) {
        const value = state.selections[opt.key];
        if (value == null) return;

        const swatches = document.querySelectorAll(
          '.configurator-swatch[data-option="' + opt.key + '"]'
        );
        let stillEnabled = false;
        swatches.forEach(function (sw) {
          if (sw.dataset.value === value && !sw.hasAttribute('disabled')) {
            stillEnabled = true;
          }
        });

        if (!stillEnabled) {
          state.selections[opt.key] = null;
          swatches.forEach(function (sw) {
            sw.classList.remove('is-selected');
          });
        }
      });
    });
  }

  // =========================================================================
  // Bundle-Products Stage (variant-options across multiple partner products)
  //   Stage 2 lets the customer pick a variant for every product in
  //   `custom.bundle_products`, using the same swatch UX as Stage 1. Each
  //   partner product's selected variant is resolved independently by
  //   walking that product's option positions against
  //   state.selections[opt.key], where `opt` is one of the stage's option
  //   entries that carries the partner's productId.
  // =========================================================================

  function getBundleProductsStage() {
    for (let i = 0; i < state.stages.length; i++) {
      if (state.stages[i].source === 'bundle_products') return state.stages[i];
    }
    return null;
  }

  // Customer-facing label for a bundle partner — prefers Shopify's
  // `product_type` (e.g. "Tower") over the generic "Bundled product N"
  // copy. Falls back to the product title, then to the indexed generic
  // label when neither is set.
  function getBundleProductTypeLabel(entry, idx) {
    if (!entry) return 'Bundled product ' + (idx + 1);
    // `productType` is injected explicitly by Liquid (resolved via
    // `all_products[handle]`) because metafield list product refs often
    // omit `product_type` on the object returned by `| json`.
    const type = (
      entry.productType ||
      (entry.product && entry.product.product_type) ||
      ''
    ).trim();
    if (type) return type;
    const title = entry.product ? (entry.product.title || '').trim() : '';
    if (title) return title;
    return 'Bundled product ' + (idx + 1);
  }

  // Group the stage's option entries by productId so per-product
  // resolution doesn't re-walk the whole options array repeatedly. The
  // shape is { [productId]: [opt, opt, ...] }. Returns an empty object
  // when there's no bundle_products stage at all (i.e. configurator is
  // active but no partner products are configured — edge case, render
  // gate normally prevents it).
  function getBundleOptionsByProduct() {
    const stage = getBundleProductsStage();
    const byProduct = {};
    if (!stage) return byProduct;
    (stage.options || []).forEach(function (opt) {
      if (opt.productId == null) return;
      const id = String(opt.productId);
      if (!byProduct[id]) byProduct[id] = [];
      byProduct[id].push(opt);
    });
    return byProduct;
  }

  // Resolve the variant the customer has chosen for a single bundle
  // product. Returns null until every option group for that product is
  // chosen, so partial selections don't silently leak into the running
  // total or the cart payload.
  //
  // Special case: products with only a default variant don't render any
  // option groups on the stage (Liquid loops `options_with_values` and
  // emits nothing), so we short-circuit to that single variant rather
  // than treating the absence of selections as "unresolved". This lets
  // a merchant ship simple no-options partners (e.g. a single SKU
  // warranty product) in `custom.bundle_products` without breaking
  // pricing or add-to-cart.
  function findBundleVariantForProduct(productId) {
    const entry = bundleProductsById[String(productId)];
    if (!entry || !entry.variants || !entry.variants.length) return null;

    const opts = getBundleOptionsByProduct()[String(productId)] || [];
    if (!opts.length) return entry.variants[0] || null;

    const allChosen = opts.every(function (opt) {
      return state.selections[opt.key] != null;
    });
    if (!allChosen) return null;

    return entry.variants.find(function (v) {
      return opts.every(function (opt) {
        const key = 'option' + (opt.position || 1);
        return v[key] === state.selections[opt.key];
      });
    }) || null;
  }

  // All currently-resolved bundle product variants. Each entry carries the
  // product + matching variant so callers (summary / breakdown / add-to-
  // cart) can render and post in a single pass. Products without a
  // fully-chosen selection are skipped, mirroring the old bundle_pair
  // behaviour where the line wouldn't surface until the customer had
  // engaged with that picker.
  function findBundleProductVariants() {
    const resolved = [];
    bundleProducts.forEach(function (entry) {
      if (!entry || !entry.product) return;
      const variant = findBundleVariantForProduct(entry.product.id);
      if (variant) {
        resolved.push({
          productId: String(entry.product.id),
          product: entry.product,
          productType: entry.productType || '',
          variant: variant,
        });
      }
    });
    return resolved;
  }

  function getBundleProductsPrice() {
    // Sum of every resolved bundle product variant — partial selections
    // contribute 0, so the running total only includes products the
    // customer has actually finished picking. Mirrors the bundle_pair-era
    // semantics where an unfinished partner didn't count toward the
    // footer until the customer engaged with it.
    return findBundleProductVariants().reduce(function (sum, entry) {
      return sum + (typeof entry.variant.price === 'number' ? entry.variant.price : 0);
    }, 0);
  }

  // True iff Stage 2 is fully complete — i.e. every option group across
  // every bundle product has a chosen value. Used by both the bundle
  // discount gate (saving only kicks in when the whole bundle is built)
  // and the validation system.
  function isBundleProductsStageFullyChosen() {
    const stage = getBundleProductsStage();
    if (!stage) return true; // no Stage 2 = vacuously complete
    return (stage.options || []).every(function (opt) {
      return state.selections[opt.key] != null;
    });
  }

  // Live price for the main reformer based on the currently-chosen frame +
  // upholstery. Falls back to the inject-time `basePrice` when the user
  // hasn't completed Stage 1 yet so footers still show a sensible "From" value.
  function getMainProductPrice() {
    const variant = findVariant(state.selections.frame, state.selections.upholstery);
    if (variant && typeof variant.price === 'number') return variant.price;
    return basePrice;
  }

  // Bundle saving — applied only when Stage 1 (frame + upholstery) AND
  // every Stage 2 option group (across every product in bundle_products)
  // have been chosen. The value and type come from the
  // `custom.bundle_discount_value` and `custom.bundle_discount_type`
  // product metafields:
  //   Percentage → round(eligible × value / 100)
  //   Fixed      → value × 100 (treat metafield as currency units, e.g. £20)
  // The result is clamped to the eligible amount so the discount can never
  // exceed the price of the required products combined.
  function getBundleDiscount() {
    if (!bundleDiscount.value || bundleDiscount.value <= 0) return 0;
    if (state.selections.frame == null || state.selections.upholstery == null) return 0;
    if (!isBundleProductsStageFullyChosen()) return 0;

    const mainVariant = findVariant(state.selections.frame, state.selections.upholstery);
    if (!mainVariant) return 0;

    const bundleVariants = findBundleProductVariants();
    // If Stage 2 has products but we couldn't resolve every one, bail —
    // this shouldn't happen when `isBundleProductsStageFullyChosen()` is
    // true, but the guard keeps us honest in case of mismatched variant
    // data.
    const bpStage = getBundleProductsStage();
    if (bpStage && bundleVariants.length !== bundleProducts.length) return 0;

    const bpSum = bundleVariants.reduce(function (sum, entry) {
      return sum + (entry.variant.price || 0);
    }, 0);
    const eligible = (mainVariant.price || 0) + bpSum;
    let discount = 0;

    if (bundleDiscount.type === 'percentage') {
      discount = Math.round(eligible * (bundleDiscount.value / 100));
    } else if (bundleDiscount.type === 'fixed') {
      // Merchant enters the value in currency units (e.g. `20` → £20.00).
      discount = Math.round(bundleDiscount.value * 100);
    }

    return Math.min(Math.max(discount, 0), eligible);
  }

  // =========================================================================
  // Optional Items (Stage 3)
  //   Each card sits in `state.selections.accessories[productId]` with:
  //     { included, variantId, title, price }
  //   The dropdown is a single combined picker (one <option> per variant),
  //   so the chosen variant id is read directly from the <select>'s value.
  //   Cards toggled on without a chosen variant are flagged visually and
  //   skipped at checkout, so they never poison the cart URL.
  // =========================================================================

  function findOptionalVariant(productId, variantId) {
    const data = optionalItemsById[productId];
    if (!data || variantId == null) return null;
    return (data.variants || []).find(function (v) { return v.id === variantId; }) || null;
  }

  function toggleOptionalItem(card) {
    const productId = card.dataset.optionalProductId;
    if (!productId) return;
    const entry = state.selections.accessories[productId];
    if (!entry) return;

    entry.included = !entry.included;
    card.classList.toggle('is-selected', entry.included);
    card.setAttribute('aria-checked', String(entry.included));

    refreshOptionalCardHint(card, entry);
    buildSelectionsSummary();
    updateFooterPrices();
    // Toggling can leave the stage in an "included but no variant" state,
    // which `isStageValid` now treats as invalid — re-evaluate so Next /
    // tab clicks lock immediately.
    updateStageLockState();
  }

  function selectOptionalVariant(selectEl) {
    const productId = selectEl.dataset.optionalProductId;
    if (!productId) return;
    const entry = state.selections.accessories[productId];
    if (!entry) return;

    const variantId = selectEl.value ? Number(selectEl.value) : null;
    entry.variantId = Number.isFinite(variantId) ? variantId : null;

    const variant = findOptionalVariant(productId, entry.variantId);
    if (variant && typeof variant.price === 'number') {
      entry.price = variant.price;
    }

    // Picking a real variant auto-includes the item — the dropdown is the
    // primary inclusion mechanism for multi-variant items. Picking the
    // placeholder leaves `included` alone so the user can re-pick (or use
    // the card toggle to explicitly remove).
    if (entry.variantId != null) {
      entry.included = true;
    }

    const card = selectEl.closest('.configurator-card--optional');
    if (card) {
      card.classList.toggle('is-selected', entry.included);
      card.setAttribute('aria-checked', String(entry.included));
      refreshOptionalCardHint(card, entry);
    }
    buildSelectionsSummary();
    updateFooterPrices();
    // Variant pick may resolve a previously-incomplete card — re-evaluate
    // so Next unlocks the moment every included card has a variant chosen.
    updateStageLockState();
  }

  function refreshOptionalCardHint(card, entry) {
    const hint = card.querySelector('[data-optional-hint]');
    if (!hint) return;
    const showHint = entry.included && entry.variantId == null;
    hint.hidden = !showHint;
    card.classList.toggle('is-incomplete', showHint);
  }

  // =========================================================================
  // Image Update
  //   The modal's hero image follows whichever product is "live" on the
  //   current stage: main product on source='product' (or any non-variant
  //   stage), and the most recently interacted bundle partner on
  //   source='bundle_products' (falling back to the first bundle product
  //   when nothing has been touched yet).
  // =========================================================================

  function updateProductImage() {
    const img = document.getElementById('configurator-img');
    if (!img) return;

    const stage = getStageByIndex(state.currentStageIndex);
    const useBundleProducts =
      stage && stage.kind === 'variant-options' && stage.source === 'bundle_products';

    const newSrc = useBundleProducts ? resolveBundleProductsImage() : resolveMainImage();
    if (!newSrc || newSrc === img.src) return;

    img.classList.add('is-loading');
    const tempImg = new Image();
    tempImg.onload = function () {
      img.src = newSrc;
      img.classList.remove('is-loading');
    };
    tempImg.src = newSrc;
  }

  function resolveMainImage() {
    if (variants.length) {
      const variant = findVariant(state.selections.frame, state.selections.upholstery);
      if (variant && variant.featured_image) return variant.featured_image.src;
    }
    return productData ? productData.featured_image : null;
  }

  function resolveBundleProductsImage() {
    // Prefer the bundle product the customer is actively configuring; fall
    // back to the first bundle product so the image panel never goes
    // blank during Stage 2. If that partner already has a resolved
    // variant with a featured image we use it (so the swatch click
    // updates the image), otherwise we fall back to the partner's
    // product-level featured image.
    const activeEntry =
      (activeBundleProductId && bundleProductsById[activeBundleProductId]) ||
      bundleProducts[0] ||
      null;
    if (!activeEntry || !activeEntry.product) return null;

    const variant = findBundleVariantForProduct(activeEntry.product.id);
    if (variant && variant.featured_image) return variant.featured_image.src;
    return activeEntry.product.featured_image || null;
  }

  function findVariant(frameValue, upholsteryValue) {
    return variants.find(function (v) {
      const frameOk =
        frameValue == null ||
        frameOptionIndex < 0 ||
        v['option' + (frameOptionIndex + 1)] === frameValue;
      const upholsteryOk =
        upholsteryValue == null ||
        upholsteryOptionIndex < 0 ||
        v['option' + (upholsteryOptionIndex + 1)] === upholsteryValue;
      return frameOk && upholsteryOk;
    });
  }

  // =========================================================================
  // Price Calculation
  //   total = main product variant + Σ bundle product variants + accessories
  //   (Promotional / free-item logic will be layered in once Rebuy is wired
  //   up — see the brief.)
  // =========================================================================

  function calculateTotal() {
    let total = getMainProductPrice() + getBundleProductsPrice();
    total += getAccessoriesTotal();
    total -= getBundleDiscount();
    return Math.max(total, 0);
  }

  function getAccessoriesTotal() {
    let sum = 0;
    Object.values(state.selections.accessories || {}).forEach(function (acc) {
      if (acc && acc.included && acc.variantId != null && typeof acc.price === 'number') {
        sum += acc.price;
      }
    });
    return sum;
  }

  // Currency-aware money formatter. Mirrors the Intl-based pattern used in
  // `quick-add-modal.js`, but reads the active currency from the injected
  // configurator data so it follows Shopify Markets context. The formatter
  // is memoised — it's only rebuilt if Intl rejects the currency code.
  function formatMoney(cents) {
    if (!moneyFormatter) {
      try {
        moneyFormatter = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currencyCode || 'GBP',
        });
      } catch (e) {
        moneyFormatter = {
          format: function (n) {
            return (n || 0).toFixed(2);
          },
        };
      }
    }
    return moneyFormatter.format((cents || 0) / 100);
  }

  function updateFooterPrices() {
    const total = calculateTotal();
    const totalStr = formatMoney(total);
    const baseStr = formatMoney(basePrice);

    // Per-stage footer price displays — driven by the stage's `price` field
    // ('base' or 'total'). The data-attribute is rendered by Liquid, so we
    // just look up which stage owns each element and pick the right value.
    document.querySelectorAll('[data-stage-price-display]').forEach(function (el) {
      const stage = getStageById(el.dataset.stagePriceDisplay);
      if (!stage) return;
      el.textContent = stage.price === 'total' ? totalStr : baseStr;
    });

    // Bundle saving — only surfaces once Stage 1 + Stage 2 are fully chosen
    // (see `getBundleDiscount`). The same value drives the review row so the
    // footer and breakdown can never disagree.
    const discount = getBundleDiscount();
    const savingStr = discount > 0 ? 'You save: ' + formatMoney(discount) : '';
    document.querySelectorAll('.js-footer-saving').forEach(function (el) {
      el.textContent = savingStr;
    });

    const checkoutBtn = document.getElementById('configurator-checkout');
    if (checkoutBtn) {
      checkoutBtn.textContent = 'Add to Cart — ' + totalStr;
    }
  }

  // =========================================================================
  // Review Page
  // =========================================================================

  function buildReviewPage() {
    buildSelectionsSummary();
    buildPricingBreakdown();
  }

  function buildSelectionsSummary() {
    const container = document.getElementById('review-selections');
    if (!container) return;

    container.innerHTML = '';

    function addRow(label, valueHtml) {
      const row = document.createElement('div');
      row.className = 'configurator-review__row';
      row.innerHTML =
        '<span class="configurator-review__row-label">' + escapeHtml(label) + '</span>' +
        '<span class="configurator-review__row-value">' + valueHtml + '</span>';
      container.appendChild(row);
    }

    // Variant-options stages — Stage 1 (source='product') renders one
    // row per option group; Stage 2 (source='bundle_products') collapses
    // every partner into a single row labelled with the partner's
    // `product_type` (e.g. "Tower"), with the chosen variant shown as
    // the row's value.
    state.stages.forEach(function (stage) {
      if (stage.kind !== 'variant-options') return;

      if (stage.source === 'bundle_products') {
        bundleProducts.forEach(function (entry, idx) {
          if (!entry || !entry.product) return;
          const label = getBundleProductTypeLabel(entry, idx);

          // Prefer the fully-resolved variant's pre-joined title
          // ("Black / M") — it's exactly the shape we want in the
          // value. Fall back to a partial-selection string when the
          // customer hasn't completed this product's options yet, so
          // partial state is still visible (handy if the customer
          // reaches the review pane via tab navigation).
          const variant = findBundleVariantForProduct(entry.product.id);
          let valueText;
          if (variant && variant.title && variant.title !== 'Default Title') {
            valueText = variant.title;
          } else if (variant) {
            // Default-only product — use the product title so the row
            // still says something meaningful.
            valueText = entry.product.title;
          } else {
            const opts = getBundleOptionsByProduct()[String(entry.product.id)] || [];
            valueText = opts
              .map(function (opt) {
                return state.selections[opt.key] || '\u2014';
              })
              .join(' / ');
          }

          addRow(label, escapeHtml(valueText || '\u2014'));
        });
        return;
      }

      (stage.options || []).forEach(function (opt) {
        addRow(opt.label, escapeHtml(state.selections[opt.key] || '\u2014'));
      });
    });

    // Optional items (Stage 3) — only those that are toggled on AND have
    // a chosen variant. The variant's `title` is Shopify's pre-joined
    // "Option1 / Option2" string, so we just append it after the product
    // title (skipping the noise default "Default Title").
    Object.entries(state.selections.accessories || {}).forEach(function (entry) {
      const productId = entry[0];
      const acc = entry[1];
      if (!acc || !acc.included || acc.variantId == null) return;

      const variant = findOptionalVariant(productId, acc.variantId);
      const variantTitle =
        variant && variant.title && variant.title !== 'Default Title' ? variant.title : '';
      const valueText = variantTitle ? acc.title + ' \u2014 ' + variantTitle : acc.title;
      addRow('Optional', escapeHtml(valueText));
    });

    // "Included in your purchase" items — appended at the end so the
    // bonuses sit visually below the customer's own selections. The data
    // is extracted once during `populateIncludedInPurchase()` from the
    // PDP's `.included-in-purchase` block, so adding / renaming items in
    // the section editor automatically flows through to this list. Items
    // with the merchant's "Free" checkbox render a `--free` value badge;
    // others render the merchant-entered price string verbatim.
    includedInPurchaseItems.forEach(function (item) {
      if (!item || !item.title) return;
      const valueHtml = item.isFree
        ? '<span class="configurator-review__value--free">Free</span>'
        : escapeHtml(item.priceText || '');
      addRow(item.title, valueHtml);
    });
  }

  function buildPricingBreakdown() {
    // Show the live main-product variant price on the breakdown's base row
    // so it tracks frame/upholstery selections, not the inject-time floor.
    const baseEl = document.getElementById('review-base-price');
    if (baseEl) baseEl.textContent = formatMoney(getMainProductPrice());

    // One row per resolved bundle product. We rebuild this container on
    // every review render rather than mutating in place — there's at
    // most a handful of partner products and the markup is trivial, so
    // the simplicity wins. Products without a fully-chosen variant are
    // skipped (mirrors `getBundleProductsPrice()` and `findBundle-
    // ProductVariants()`).
    const bpRowsContainer = document.getElementById('review-bundle-products-rows');
    if (bpRowsContainer) {
      bpRowsContainer.innerHTML = '';
      findBundleProductVariants().forEach(function (entry) {
        const price = typeof entry.variant.price === 'number' ? entry.variant.price : 0;
        if (price <= 0) return;
        const row = document.createElement('div');
        row.className = 'configurator-review__price-row';
        const bpIdx = bundleProducts.findIndex(function (bp) {
          return bp && bp.product && String(bp.product.id) === String(entry.product.id);
        });
        row.innerHTML =
          '<span>' +
          escapeHtml(getBundleProductTypeLabel(entry, bpIdx >= 0 ? bpIdx : 0)) +
          '</span>' +
          '<span>+' + formatMoney(price) + '</span>';
        bpRowsContainer.appendChild(row);
      });
    }

    const accRow = document.getElementById('review-accessories-row');
    const accEl = document.getElementById('review-accessories-price');
    if (accRow && accEl) {
      const accTotal = getAccessoriesTotal();
      if (accTotal > 0) {
        accEl.textContent = '+' + formatMoney(accTotal);
        accRow.style.display = 'flex';
      } else {
        accRow.style.display = 'none';
      }
    }

    // Bundle saving row — mirrors the footer line. Hidden until both Stage 1
    // and Stage 2 are complete and the metafield-driven discount is > 0.
    // Label is qualified with the percentage for Percentage-type discounts;
    // for Fixed it stays as a plain "Bundle saving".
    const savingsRow = document.getElementById('review-savings-row');
    const savingsEl = document.getElementById('review-savings-price');
    const savingsLabel = savingsRow && savingsRow.querySelector('[data-savings-label]');
    const discount = getBundleDiscount();
    if (savingsRow && savingsEl) {
      if (discount > 0) {
        if (savingsLabel) {
          savingsLabel.textContent =
            bundleDiscount.type === 'percentage'
              ? 'Bundle saving (' + bundleDiscount.value + '%)'
              : 'Bundle saving';
        }
        savingsEl.textContent = '\u2212' + formatMoney(discount);
        savingsRow.style.display = 'flex';
      } else {
        savingsRow.style.display = 'none';
      }
    }

    const totalEl = document.getElementById('review-total');
    if (totalEl) totalEl.textContent = formatMoney(calculateTotal());
  }

  // =========================================================================
  // Checkout
  // =========================================================================

  function isValidVariantId(id) {
    // Reject null, undefined, the literal string "null", and anything that
    // isn't a positive integer-like value. Prevents broken IDs from being
    // interpolated into the cart permalink (which would 410).
    if (id === null || id === undefined) return false;
    if (typeof id === 'string' && id.toLowerCase() === 'null') return false;
    const n = Number(id);
    return Number.isFinite(n) && n > 0;
  }

  // Rebuy emits a small family of cart events when it injects
  // gift-with-purchase line items. We listen to all of them and use an
  // idle timer so the moment Rebuy has stopped mutating the cart we
  // navigate to checkout — that way checkout sees the gifts that have
  // just been added, and the customer doesn't pay for them.
  const REBUY_CART_EVENTS = [
    'rebuy:cart.change',
    'rebuy:cart.updated',
    'rebuy:cart.add',
    'rebuy:cart.remove',
  ];

  function handleAddToCart() {
    const items = [];

    // Main product variant — derived from the frame/upholstery selections
    const variant = findVariant(state.selections.frame, state.selections.upholstery);
    if (variant && isValidVariantId(variant.id)) {
      items.push({ id: Number(variant.id), quantity: 1 });
    } else if (variants.length > 0 && isValidVariantId(variants[0].id)) {
      items.push({ id: Number(variants[0].id), quantity: 1 });
    }

    // Bundle product variants — one per partner in `custom.bundle_products`.
    // We only push variants that resolve from the customer's Stage 2
    // selections; partial selections are dropped rather than auto-filled
    // because Stage 2 is required and the review step is unreachable
    // until every partner has been chosen, so any unresolved entries here
    // mean the customer is bypassing validation.
    findBundleProductVariants().forEach(function (entry) {
      if (entry.variant && isValidVariantId(entry.variant.id)) {
        items.push({ id: Number(entry.variant.id), quantity: 1 });
      }
    });

    // Optional items — only included items with a fully-resolved variant.
    Object.values(state.selections.accessories || {}).forEach(function (acc) {
      if (acc && acc.included && isValidVariantId(acc.variantId)) {
        items.push({ id: Number(acc.variantId), quantity: 1 });
      }
    });

    if (items.length === 0) {
      console.warn('Purchase Configurator: no valid items to add');
      return;
    }

    const checkoutBtn = document.getElementById('configurator-checkout');
    const originalLabel = checkoutBtn ? checkoutBtn.textContent : '';

    function restoreButton() {
      if (!checkoutBtn) return;
      checkoutBtn.disabled = false;
      checkoutBtn.textContent = originalLabel;
    }

    if (checkoutBtn) {
      checkoutBtn.disabled = true;
      checkoutBtn.textContent = 'Adding to cart\u2026';
    }

    // POSTing the full items array in one call gives Rebuy a single cart
    // mutation to react to, rather than N — gift rules evaluate once.
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: items }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/add.js failed with status ' + res.status);
        return res.json();
      })
      .then(function () {
        // Hold the configurator open until Rebuy has finished injecting
        // gift-with-purchase items — closing earlier would let the smart
        // cart open mid-injection and the customer would see gifts pop
        // in one by one. The footer total stays current via the live
        // price label until the close fires.
        waitForRebuyIdle(function () {
          restoreButton();
          closeConfigurator();
        });
      })
      .catch(function (err) {
        // No fallback redirect — the goal is to add to cart and surface
        // the smart cart, not to send the customer to checkout. Restore
        // the button so they can retry the add manually.
        console.error('Purchase Configurator: cart add failed', err);
        restoreButton();
      });
  }

  // Wait for Rebuy to finish injecting any gift-with-purchase line items
  // (it observes cart mutations and posts its own `/cart/add.js` requests),
  // then invoke `done`.
  //
  // Strategy: listen for any `rebuy:cart.*` event after the customer's
  // `/cart/add.js` resolves. Each event resets a short idle timer; when
  // the idle timer fires we assume Rebuy is done and run the callback.
  // A hard ceiling caps the total wait so a slow or unresponsive Rebuy
  // can never strand the customer with a disabled button.
  //   • IDLE_MS  — how long with no Rebuy events before we declare done
  //   • MAX_MS   — absolute upper bound on total wait
  // If Rebuy isn't doing anything for this product, IDLE_MS elapses with
  // no Rebuy traffic and the callback fires after a barely-perceptible
  // wait beyond the cart API roundtrip.
  function waitForRebuyIdle(done) {
    const IDLE_MS = 600;
    const MAX_MS = 3000;
    let idleTimer = null;
    let maxTimer = null;
    let finished = false;

    function cleanup() {
      if (idleTimer) clearTimeout(idleTimer);
      if (maxTimer) clearTimeout(maxTimer);
      REBUY_CART_EVENTS.forEach(function (ev) {
        window.removeEventListener(ev, onRebuyEvent);
        document.removeEventListener(ev, onRebuyEvent);
      });
    }

    function finish() {
      if (finished) return;
      finished = true;
      cleanup();
      try { done(); } catch (e) { console.error(e); }
    }

    function resetIdle() {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(finish, IDLE_MS);
    }

    function onRebuyEvent() {
      resetIdle();
    }

    REBUY_CART_EVENTS.forEach(function (ev) {
      window.addEventListener(ev, onRebuyEvent);
      document.addEventListener(ev, onRebuyEvent);
    });

    resetIdle();
    maxTimer = setTimeout(finish, MAX_MS);
  }

  // =========================================================================
  // Helpers
  // =========================================================================

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // =========================================================================
  // Bootstrap
  // =========================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
